import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, DollarSign, Sparkles, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import AdminPriceListPage from "@/pages/AdminPriceListPage";
import AdminFabricManagementPage from "@/pages/AdminFabricManagementPage";

/**
 * Unified Pricing + Fabrics surface — merges what used to be `/admin/price-list`
 * and `/admin/fabric-management` into a single tabbed page. Each child still
 * runs as-is (state, fetches, dialogs, modals); we just hide their own header
 * via the new `hideHeader` prop and host them inside a shared chrome.
 *
 * URL contract:
 *   /admin/pricing-fabrics            → defaults to Pricing tab
 *   /admin/pricing-fabrics?tab=fabrics → opens Fabrics & Buttons tab
 *   /admin/price-list                 → still works (renders this page, Pricing tab)
 *   /admin/fabric-management          → still works (renders this page, Fabrics tab)
 */
export default function AdminPricingFabricsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pick the tab from URL — supports both ?tab=fabrics and the legacy routes
  const initialTab =
    location.pathname.includes("fabric-management") ||
    new URLSearchParams(location.search).get("tab") === "fabrics"
      ? "fabrics"
      : "pricing";

  const [tab, setTab] = useState(initialTab);

  // Keep the URL query in sync so refresh / share-link lands on the same tab,
  // without unmounting either child (which would lose all in-progress edits).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (tab === "fabrics" && params.get("tab") !== "fabrics") {
      params.set("tab", "fabrics");
      navigate(`/admin/pricing-fabrics?${params.toString()}`, { replace: true });
    } else if (tab === "pricing" && params.get("tab")) {
      navigate(`/admin/pricing-fabrics`, { replace: true });
    }
    // We intentionally do not depend on location/navigate — only tab transitions
    // should trigger URL updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const TabBtn = ({ id, icon: Icon, label, color }) => {
    const active = tab === id;
    return (
      <button
        type="button"
        onClick={() => setTab(id)}
        data-testid={`pricing-fabrics-tab-${id}`}
        className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
          active
            ? `border-${color}-400 text-white`
            : "border-transparent text-slate-400 hover:text-white hover:bg-white/[0.02]"
        }`}
        style={active ? { borderColor: color === "emerald" ? "#34d399" : "#f59e0b" } : {}}
      >
        <Icon size={16} className={active ? (color === "emerald" ? "text-emerald-400" : "text-amber-400") : ""} />
        {label}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      {/* Shared chrome */}
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/admin")}
                className="text-slate-400 hover:text-white"
                data-testid="pricing-fabrics-back-btn"
              >
                <ArrowLeft size={22} />
              </button>
              <div>
                <h1 className="text-xl font-bold">Pricing &amp; Fabrics</h1>
                <p className="text-sm text-slate-400">
                  {tab === "pricing"
                    ? "Construction tiers, markups and per-garment retail pricing"
                    : "Add, edit and remove fabrics, linings and buttons across the whole app"}
                </p>
              </div>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex items-center gap-1 mt-3 -mb-4">
            <TabBtn id="pricing" icon={DollarSign} label="Pricing" color="emerald" />
            <TabBtn id="fabrics" icon={Sparkles} label="Fabrics & Buttons" color="amber" />
            <div className="flex-1" />
            {tab === "fabrics" && (
              <Button
                size="sm"
                variant="outline"
                className="border-white/20"
                onClick={() => {
                  // The Fabric Management page exposes its add CTA via the "fm-add-btn"
                  // data-testid; click it programmatically so the user gets the same flow
                  // even though the child's own header is hidden.
                  document.querySelector('[data-testid="fm-add-btn"]')?.click();
                }}
                data-testid="pricing-fabrics-add-btn"
              >
                <Plus size={14} className="mr-1" /> Add Fabric / Button
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Children — kept mounted so each tab retains its scroll position
          and in-progress edits when the user toggles back. */}
      <div style={{ display: tab === "pricing" ? "block" : "none" }}>
        <AdminPriceListPage hideHeader />
      </div>
      <div style={{ display: tab === "fabrics" ? "block" : "none" }}>
        <AdminFabricManagementPage hideHeader />
      </div>
    </div>
  );
}
